'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { watchtowerPosts } from '../../lib/api/watchtower';
import { WatchtowerPostWithAuthor } from '../../lib/types/watchtower';
import { createClient } from '../../lib/supabase/client';
import BrailleSpinner from '../components/BrailleSpinner';
import { tickerToSlug } from '@/lib/ticker-url';
import { STOCK_LINK_CLASS } from '@/lib/stock-link-style';
import { watchtowerPostPath } from '@/lib/watchtower/slug';
import { HUB_PANEL_SURFACE } from '@/lib/hub/hub-panel-surface';

export default function WatchtowerPage() {
  const router = useRouter();
  const [posts, setPosts] = useState<WatchtowerPostWithAuthor[]>([]);
  const [newPost, setNewPost] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newImage, setNewImage] = useState('');
  const [newImages, setNewImages] = useState<string[]>([]);
  const [newTags, setNewTags] = useState<string[]>([]);
  const [isComposing, setIsComposing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editImage, setEditImage] = useState('');
  const [editImages, setEditImages] = useState<string[]>([]);
  const [editTags, setEditTags] = useState<string[]>([]);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);
  const [displayCount, setDisplayCount] = useState<number>(12);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [editTagInput, setEditTagInput] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showEditTagDropdown, setShowEditTagDropdown] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState<Record<string, boolean>>({});
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter existing tags for autocomplete suggestions
  useEffect(() => {
    const query = isComposing ? tagInput : (isEditing ? editTagInput : '');
    if (!query || query.length < 1) {
      setTagSuggestions([]);
      return;
    }
    const allTags = getAllExistingTags();
    const currentTags = isComposing ? newTags : editTags;
    const q = query.toLowerCase();
    const filtered = allTags
      .filter(tag => tag.toLowerCase().includes(q) && !currentTags.includes(tag));
    setTagSuggestions(filtered);
  }, [tagInput, editTagInput, isComposing, isEditing, posts, newTags, editTags]);

  // Load posts from Supabase and check admin status
  useEffect(() => {
    loadPosts();
    checkAdminStatus();
    
    // Listen for auth state changes to update user data
    const supabase = createClient();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        checkAdminStatus();
      } else if (event === 'SIGNED_OUT') {
        setUserEmail(null);
        setUserId(null);
        setUserAvatar(null);
        setIsAdmin(false);
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Restore display count and scroll position once after posts load (two-phase approach)
  const scrollRestoredRef = useRef(false);
  const pendingScrollRef = useRef<number | null>(null);

  // Phase 1: When posts load, read saved state and restore displayCount
  useEffect(() => {
    if (posts.length > 0 && !scrollRestoredRef.current) {
      scrollRestoredRef.current = true;
      const savedScrollPosition = sessionStorage.getItem('watchtowerScrollPosition');
      const savedDisplayCount = sessionStorage.getItem('watchtowerDisplayCount');

      if (savedScrollPosition) {
        const targetY = parseInt(savedScrollPosition);
        sessionStorage.removeItem('watchtowerScrollPosition');

        if (savedDisplayCount) {
          const count = parseInt(savedDisplayCount);
          sessionStorage.removeItem('watchtowerDisplayCount');
          if (count !== displayCount) {
            pendingScrollRef.current = targetY;
            setDisplayCount(count);
          } else {
            requestAnimationFrame(() => { window.scrollTo(0, targetY); });
          }
        } else {
          requestAnimationFrame(() => { window.scrollTo(0, targetY); });
        }
      }
    }
  }, [posts]);

  // Phase 2: After displayCount updates and DOM re-renders, perform the scroll
  useEffect(() => {
    if (pendingScrollRef.current !== null && posts.length > 0) {
      const targetY = pendingScrollRef.current;
      const tryRestore = (attempts = 0) => {
        requestAnimationFrame(() => {
          window.scrollTo(0, targetY);
          if (Math.abs(window.scrollY - targetY) > 50 && attempts < 25) {
            setTimeout(() => tryRestore(attempts + 1), 80);
          } else {
            pendingScrollRef.current = null;
          }
        });
      };
      tryRestore();
    }
  }, [displayCount, posts.length]);

  const checkAdminStatus = async () => {
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || null);
        setUserId(user.id);
        fetch('/api/auth/check-admin').then(r => r.json()).then(d => setIsAdmin(!!d.isAdmin)).catch(() => {});
        
        // Load user profile and avatar
        const { data: profile } = await supabase
          .from('profiles')
          .select('avatar_url, full_name')
          .eq('id', user.id)
          .single();
        
        if (profile?.avatar_url) {
          setUserAvatar(profile.avatar_url);
        }
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
    }
  };

  const loadPosts = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const data = await watchtowerPosts.getAll({ limit: 500 });
      setPosts(data);
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Failed to load posts');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    // Admin can delete any post
    if (!isAdmin) return;
    const ok = typeof window !== 'undefined' ? window.confirm('Delete this post? This action cannot be undone.') : false;
    if (!ok) return;
    try {
      // Find the post to get its images
      const post = posts.find(p => p.id === postId);
      if (post) {
        // Delete images from storage - prioritize image_urls, fallback to image_url
        if (post.image_urls && post.image_urls.length > 0) {
          for (const imageUrl of post.image_urls) {
            await deleteImageFromStorage(imageUrl);
          }
        } else if (post.image_url) {
          await deleteImageFromStorage(post.image_url);
        }
      }
      
      await watchtowerPosts.delete(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post');
    }
  };

  const getAllExistingTags = (): string[] => {
    const tagSet = new Set<string>();
    posts.forEach(post => {
      if (post.tags) {
        post.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  };

  const renderTitleWithScaledEmojis = (title: string) => {
    if (!title) return null;
    const emojiRegex = /([\u{1F300}-\u{1F9FF}]|[\u{1F1E0}-\u{1F1FF}]{2}|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}])/gu;
    const parts: Array<{ type: 'emoji' | 'text', content: string }> = [];
    let lastIndex = 0;
    let match;
    
    while ((match = emojiRegex.exec(title)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: title.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'emoji', content: match[0] });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < title.length) {
      parts.push({ type: 'text', content: title.slice(lastIndex) });
    }
    
    return parts.map((part, idx) => 
      part.type === 'emoji' ? (
        <span key={idx} style={{ fontSize: '0.75em', verticalAlign: 'baseline', display: 'inline-block', lineHeight: '1.2' }}>
          {part.content}
        </span>
      ) : (
        <span key={idx}>{part.content}</span>
      )
    );
  };

  const renderContentWithHighlights = (content: string) => {
    if (!content) return null;
    const paragraphs = content.split(/\n\n+/);
    
    const highlightText = (text: string) => {
      const combinedRegex = /(\*\*(.+?)\*\*)|(\$(?!\d+[BMKT]?\b)[A-Z0-9]{1,10}(?::[A-Z]{2,4})?)|(https?:\/\/[^\s<]+[^\s<.,;:!?)\]"'])|([a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}(?:\/[^\s<]*[^\s<.,;:!?)\]"'])?)/g;
      const parts: React.ReactNode[] = [];
      let lastIdx = 0;
      let match;
      let key = 0;

      while ((match = combinedRegex.exec(text)) !== null) {
        if (match.index > lastIdx) {
          parts.push(text.slice(lastIdx, match.index));
        }
        if (match[1]) {
          parts.push(
            <strong key={key++} style={{ color: '#ffffff', fontWeight: 600 }}>
              {match[2]}
            </strong>
          );
        } else if (match[3]) {
          const token = match[3];
          const ticker = token.slice(1);
          const fullTicker = ticker.includes(':') ? ticker : `${ticker}:USA`;
          parts.push(
            <a
              key={key++}
              href={`/stock/${tickerToSlug(fullTicker)}`}
              className={STOCK_LINK_CLASS}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.stopPropagation(); }}
              style={{ color: '#d4a54a', fontWeight: 600, cursor: 'pointer' }}
            >
              {token}
            </a>
          );
        } else if (match[4] || match[5]) {
          const url = match[4] || match[5];
          const href = url.startsWith('http') ? url : `https://${url}`;
          parts.push(
            <a
              key={key++}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.stopPropagation(); }}
              style={{ color: '#3bd576', textDecoration: 'none', borderBottom: '1px solid rgba(59,213,118,0.3)' }}
            >
              {url}
            </a>
          );
        }
        lastIdx = match.index + match[0].length;
      }
      if (lastIdx < text.length) {
        parts.push(text.slice(lastIdx));
      }
      return parts;
    };

    return paragraphs.map((para, i) => {
      const lines = para.split(/\n/);
      return (
        <p
          key={i}
          style={{
            marginBottom: i < paragraphs.length - 1 ? '10px' : '0',
            fontSize: 14,
            fontWeight: 400,
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            lineHeight: 1.65,
            color: 'rgba(255,255,255,0.65)',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {lines.map((line, j) => (
            <span key={j}>
              {j > 0 && <br />}
              {highlightText(line)}
            </span>
          ))}
        </p>
      );
    });
  };

  const handleStartEdit = (postId: string, title: string | null, content: string | null, imageUrl?: string | null, imageUrls?: string[] | null, tags?: string[] | null) => {
    if (!isAdmin) return;
    setEditingPostId(postId);
    setIsEditing(true);
    setEditTitle(title || '');
    setEditContent(content || '');
    setEditImage(imageUrl || '');
    setEditImages(imageUrls || []);
    setEditTags(tags || []);
  };

  const [savingEdit, setSavingEdit] = useState(false);
  const handleSaveEdit = async () => {
    if (!editingPostId || savingEdit) return;
    setSavingEdit(true);
    try {
      await watchtowerPosts.update({
        id: editingPostId,
        title: editTitle.trim() || editContent.trim().slice(0, 100),
        content: editContent.trim() || '',
        image_urls: editImages.length ? editImages : undefined,
        tags: editTags.length > 0 ? editTags : undefined,
      });
      setPosts(prev =>
        prev.map(post =>
          post.id === editingPostId
            ? {
                ...post,
                title: editTitle.trim() || editContent.trim().slice(0, 100),
                content: editContent.trim() || '',
                image_urls: editImages.length ? editImages : post.image_urls,
                tags: editTags.length > 0 ? editTags : post.tags,
              }
            : post
        )
      );
      loadPosts(true);
      setEditingPostId(null);
      setIsEditing(false);
      setNewTitle('');
      setNewPost('');
      setNewImage('');
      setEditTitle('');
      setEditContent('');
      setEditImage('');
      setEditImages([]);
      setEditTags([]);
    } catch (err) {
      console.error('Error updating post:', err);
      alert(err instanceof Error ? err.message : 'Failed to update post');
    } finally {
      setSavingEdit(false);
    }
  };


  // Search function
  const searchPosts = (posts: WatchtowerPostWithAuthor[], query: string): WatchtowerPostWithAuthor[] => {
    if (!query.trim()) return posts;
    
    const searchTerm = query.toLowerCase().trim();
    const tagTerm = searchTerm.replace(/^#+/, '');
    return posts.filter(post => {
      const title = post.title?.toLowerCase() || '';
      const content = post.content?.toLowerCase() || '';
      const authorName = post.author_name?.toLowerCase() || '';
      const tags = post.tags?.map(tag => tag.toLowerCase()).join(' ') || '';
      
      return title.includes(searchTerm) ||
             content.includes(searchTerm) ||
             authorName.includes(searchTerm) ||
             (tagTerm.length > 0 && tags.includes(tagTerm));
    });
  };

  // Filter posts by selected tag and search query
  const filteredPosts = (() => {
    let filtered = posts;
    
    // Apply tag filter (only when tag is selected via click)
    if (selectedTag) {
      filtered = filtered.filter(post => post.tags && post.tags.includes(selectedTag));
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      filtered = searchPosts(filtered, searchQuery);
    }
    
    return filtered;
  })();

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(12);
  }, [selectedTag, searchQuery]);

  // Posts to display (limited by displayCount)
  const postsToShow = filteredPosts.slice(0, displayCount);
  const hasMore = displayCount < filteredPosts.length;

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 12);
  };

  // Handle tag click
  const handleTagClick = (tag: string) => {
    setSelectedTag(selectedTag === tag ? null : tag);
  };


  // Handle share
  const handleShare = async (post: { id: string; slug?: string | null }) => {
    const postUrl = `${window.location.origin}${watchtowerPostPath(post)}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Watchtower Post',
          url: postUrl,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(postUrl);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      // User cancelled or error occurred
      if (err instanceof Error && err.name !== 'AbortError') {
        // Fallback: copy to clipboard
        try {
          await navigator.clipboard.writeText(postUrl);
          alert('Link copied to clipboard!');
        } catch (clipboardErr) {
          console.error('Failed to copy link:', clipboardErr);
        }
      }
    }
  };

  // Get top 10 most popular tags from all posts
  const getAllTags = (): string[] => {
    const tagCounts = new Map<string, number>();
    
    // Count tag occurrences
    posts.forEach(post => {
      if (post.tags) {
        post.tags.forEach(tag => {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        });
      }
    });
    
    // Sort by count (descending) and take top 10
    return Array.from(tagCounts.entries())
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .slice(0, 10) // Take top 10
      .map(([tag]) => tag) // Extract just the tag names
      .sort(); // Sort alphabetically for display
  };

  // Handle tag removal in edit mode
  const handleRemoveEditTag = (tagToRemove: string) => {
    setEditTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  // Handle adding new tag in edit mode
  const handleAddEditTag = (tag: string) => {
    if (tag.trim() && !editTags.includes(tag.trim())) {
      setEditTags(prev => [...prev, tag.trim()]);
    }
  };

  // Handle tag removal in new post mode
  const handleRemoveNewTag = (tagToRemove: string) => {
    setNewTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  // Handle adding new tag in new post mode
  const handleAddNewTag = (tag: string) => {
    if (tag.trim() && !newTags.includes(tag.trim())) {
      setNewTags(prev => [...prev, tag.trim()]);
    }
  };


  const DRAFT_KEY = 'watchtower_draft';

  const saveDraftToLocal = () => {
    const draft = { title: newTitle, content: newPost, images: newImages, tags: newTags, savedAt: Date.now() };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch {}
  };

  const loadDraftFromLocal = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as { title: string; content: string; images: string[]; tags: string[]; savedAt: number };
    } catch { return null; }
  };

  const clearDraftLocal = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setHasDraft(false);
  };

  const handleSaveDraft = async () => {
    if (!newTitle.trim() && !newPost.trim()) return;
    setDraftSaving(true);
    saveDraftToLocal();
    try {
      await watchtowerPosts.create({
        title: newTitle.trim() || 'Untitled draft',
        content: newPost.trim() || '',
        image_urls: newImages.length ? newImages : undefined,
        tags: newTags.length > 0 ? newTags : undefined,
        is_published: false,
      });
    } catch {}
    setDraftSaving(false);
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2000);
  };

  const handleRestoreDraft = () => {
    const draft = loadDraftFromLocal();
    if (!draft) return;
    setNewTitle(draft.title || '');
    setNewPost(draft.content || '');
    setNewImages(draft.images || []);
    setNewTags(draft.tags || []);
    setHasDraft(false);
    setIsComposing(true);
  };

  // Check for saved draft on mount
  useEffect(() => {
    const draft = loadDraftFromLocal();
    if (draft && (draft.title || draft.content)) setHasDraft(true);
  }, []);

  // Auto-save draft to localStorage while composing
  useEffect(() => {
    if (!isComposing) return;
    if (!newTitle.trim() && !newPost.trim()) return;
    const timer = setTimeout(() => saveDraftToLocal(), 1000);
    return () => clearTimeout(timer);
  }, [isComposing, newTitle, newPost, newImages, newTags]);

  const handlePost = async () => {
    if (!newTitle.trim() && !newPost.trim()) return;

    try {
      const post = await watchtowerPosts.create({
        title: newTitle.trim() || newPost.substring(0, 100),
        content: newPost.trim() || '',
        image_urls: newImages.length ? newImages : undefined,
        tags: newTags.length > 0 ? newTags : undefined,
        is_published: true,
      });
      setPosts(prev => [post as any, ...prev]);
      loadPosts(true);
      clearDraftLocal();
      
      setNewPost('');
      setNewTitle('');
      setNewImage('');
      setNewImages([]);
      setNewTags([]);
      setIsComposing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create post');
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  // Handle paste events for images
  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await uploadImage(file, Boolean(editingPostId));
        }
        break;
      }
    }
  };

  // Basic rich-text shortcuts for post body (e.g. Ctrl/Cmd + B → **bold**)
  const handleRichTextKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    value: string,
    setValue: (v: string) => void
  ) => {
    const isBoldShortcut =
      (e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B');
    if (!isBoldShortcut) return;

    e.preventDefault();
    const target = e.target as HTMLTextAreaElement;
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;

    // If nothing selected — insert empty bold markers
    if (start === end) {
      const insertion = '****';
      const newText =
        value.slice(0, start) + insertion + value.slice(end);
      setValue(newText);
      // Place caret between the asterisks
      setTimeout(() => {
        const pos = start + 2;
        target.selectionStart = pos;
        target.selectionEnd = pos;
      }, 0);
      return;
    }

    // Wrap selected text with ** **
    const selected = value.slice(start, end);
    const newText =
      value.slice(0, start) + '**' + selected + '**' + value.slice(end);
    setValue(newText);
    setTimeout(() => {
      const offset = 2;
      target.selectionStart = start + offset;
      target.selectionEnd = end + offset;
    }, 0);
  };

  // Upload image to Supabase Storage
  const uploadImage = async (file: File, isEditing = false) => {
    try {
      setUploadingImage(true);
      const supabase = createClient();
      
      // Generate filename based on file content hash to prevent duplicates
      const fileExt = file.name.split('.').pop();
      const fileBuffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', fileBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      const fileName = `${hashHex.substring(0, 16)}.${fileExt}`;
      const filePath = `watchtower-images/${fileName}`;

      // Upload file with upsert to handle duplicates gracefully
      const { data, error } = await supabase.storage
        .from('watchtower-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true  // This will overwrite if file exists
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('watchtower-images')
        .getPublicUrl(filePath);
      
      // Add to appropriate state based on editing mode
      if (isEditing) {
        setEditImages((prev: string[]) => {
          // Check if image already exists to prevent duplicates
          if (prev.includes(publicUrl)) return prev;
          return [...prev, publicUrl];
        });
      } else {
        setNewImages((prev: string[]) => {
          // Check if image already exists to prevent duplicates
          if (prev.includes(publicUrl)) return prev;
          return [...prev, publicUrl];
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle file input for images
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadImage(file, Boolean(editingPostId));
    }
  };

  // Delete image from Storage
  const deleteImageFromStorage = async (imageUrl: string) => {
    try {
      const supabase = createClient();
      // Extract file path from URL
      const url = new URL(imageUrl);
      const pathParts = url.pathname.split('/');
      const bucketName = pathParts[pathParts.length - 3];
      const filePath = pathParts.slice(-2).join('/');
      
      await supabase.storage
        .from(bucketName)
        .remove([filePath]);
    } catch (err) {
      console.error('Error deleting image from storage:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen text-white">
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 20px 40px' }}>
        {/* Header */}
        <header style={{ padding: '10px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h1 style={{ fontSize: 24, fontWeight: 600, color: '#E5E7EB', letterSpacing: '-0.01em', fontFamily: 'var(--font-inter-tight), -apple-system, BlinkMacSystemFont, sans-serif', margin: 0 }}>
                Watchtower
              </h1>
              {isAdmin && (
                <span style={{ fontSize: 10, fontWeight: 600, color: '#3bd576', letterSpacing: '0.08em', textTransform: 'uppercase' as const }}>admin</span>
              )}
            </div>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: '4px 0 0', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
            Market observations and ideas for investors
          </p>
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', marginTop: 14 }} />
        </header>

        {/* Search */}
        {!loading && posts.length > 0 && (
          <div style={{ margin: '14px 0 0' }}>
            <div style={{ position: 'relative' }}>
              <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.03)',
                  color: '#fff', fontSize: 13, outline: 'none', transition: 'border-color 0.15s',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(59,213,118,0.3)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 15, padding: 0 }}>×</button>
              )}
            </div>
            {(searchQuery || selectedTag) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                {searchQuery && <span>{filteredPosts.length} result{filteredPosts.length !== 1 ? 's' : ''}</span>}
                {selectedTag && (
                  <>
                    <span>tag: <span style={{ color: '#3bd576' }}>{selectedTag}</span></span>
                    <button onClick={() => setSelectedTag(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>clear</button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Admin compose trigger */}
        {isAdmin && !isComposing && (
          <button
            onClick={() => setIsComposing(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '10px 0', marginTop: 10, background: 'none', border: 'none',
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>+ New post</span>
          </button>
        )}

        {/* Draft restore */}
        {isAdmin && hasDraft && !isComposing && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', marginBottom: 4, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            <span>Unsaved draft</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleRestoreDraft}
                style={{ padding: '4px 12px', borderRadius: '6px', background: 'rgba(66, 219, 123, 0.15)', border: '1px solid rgba(66, 219, 123, 0.25)', color: '#42db7b', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
              >
                Restore
              </button>
              <button onClick={clearDraftLocal}
                style={{ padding: '4px 10px', borderRadius: '6px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {/* Compose Modal */}
        {isComposing && isAdmin && (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              background: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(8px)',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
              paddingTop: '3vh',
              paddingBottom: '3vh',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: '600px',
                margin: '0 16px',
                background: '#1a1a1a',
                borderRadius: isMobile ? '0' : '14px',
                border: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.07)',
                boxShadow: '0 32px 100px rgba(0, 0, 0, 0.7)',
                overflow: 'visible',
                position: 'relative',
                minHeight: isMobile ? '100vh' : 'min(85vh, 700px)',
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {userAvatar ? (
                    <img src={userAvatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} />
                  ) : (
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #42db7b, #3bc56b)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontSize: '12px', fontWeight: 700 }}>
                      {userEmail?.charAt(0).toUpperCase() || 'A'}
                    </div>
                  )}
                  <span style={{ color: '#fff', fontSize: '14px', fontWeight: 600, fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                    New post
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {(newPost.length > 0 || newTitle.length > 0) && (
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                      {newPost.trim().split(/\s+/).filter(Boolean).length} words
                    </span>
                  )}
                  <button
                    onClick={async () => {
                      if (newImages.length > 0) {
                        for (const imageUrl of newImages) { await deleteImageFromStorage(imageUrl); }
                      }
                      clearDraftLocal();
                      setIsComposing(false); setNewPost(''); setNewTitle(''); setNewImage(''); setNewImages([]); setNewTags([]); setTagInput(''); setShowTagDropdown(false);
                    }}
                    style={{
                      background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer',
                      width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: '6px', transition: 'all 0.15s ease', fontSize: '18px',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Modal body */}
              <div
                className="custom-dropdown-scrollbar"
                style={{ padding: '24px 24px', overflowY: 'auto', overscrollBehavior: 'contain', flex: 1 }}
                onPaste={handlePaste}
              >
                <textarea
                  value={newTitle}
                  ref={(el) => {
                    if (el) {
                      el.style.height = '0';
                      el.style.height = el.scrollHeight + 'px';
                    }
                  }}
                  onChange={(e) => {
                    setNewTitle(e.target.value);
                    e.target.style.height = '0';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onPaste={handlePaste}
                  placeholder="Title"
                  autoFocus
                  rows={1}
                  style={{
                    width: '100%',
                    padding: '0 0 14px 0',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                    color: '#fff',
                    fontSize: '20px',
                    fontWeight: 700,
                    outline: 'none',
                    fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    marginBottom: '16px',
                    resize: 'none',
                    overflow: 'hidden',
                    letterSpacing: '-0.01em',
                  }}
                />
                <textarea
                  value={newPost}
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                  onChange={(e) => {
                    setNewPost(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onKeyDown={(e) => {
                    handleRichTextKeyDown(e, newPost, setNewPost);
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); handlePost(); }
                    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) { e.preventDefault(); handleSaveDraft(); }
                  }}
                  onPaste={handlePaste}
                  placeholder="Write your post..."
                  style={{
                    width: '100%', padding: '0', background: 'transparent', border: 'none',
                    color: '#d4d4d4', fontSize: '15px', fontWeight: 400, lineHeight: '1.7',
                    fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    resize: 'none' as const, minHeight: '350px', outline: 'none',
                  }}
                  rows={1}
                />

                {newImages.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '16px', padding: '12px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    {newImages.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        <img src={img} alt={`Preview ${idx+1}`}
                          style={{ maxWidth: '160px', maxHeight: '120px', objectFit: 'contain', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}
                          onClick={() => setSelectedImage(img)}
                        />
                        <button
                          onClick={() => setNewImages((prev: string[]) => prev.filter((_: string, i: number) => i !== idx))}
                          style={{ position: 'absolute', top: '4px', right: '4px', width: '20px', height: '20px', background: 'rgba(220, 38, 38, 0.9)', border: 'none', borderRadius: '50%', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tags */}
                <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                  {newTags.map((tag, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '16px', background: 'rgba(66, 219, 123, 0.1)', border: '1px solid rgba(66, 219, 123, 0.2)', color: '#42db7b', fontSize: '12px', fontWeight: 600, fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                      <span>{tag}</span>
                      <button onClick={() => handleRemoveNewTag(tag)}
                        style={{ background: 'none', border: 'none', color: 'rgba(66, 219, 123, 0.5)', cursor: 'pointer', padding: '0', fontSize: '13px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#42db7b'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(66, 219, 123, 0.5)'; }}
                      >×</button>
                    </div>
                  ))}
                  <div style={{ position: 'relative', flex: 1, minWidth: '100px' }}>
                    <input type="text" value={tagInput}
                      onChange={(e) => { const v = e.target.value; setTagInput(v); setShowTagDropdown(v.length >= 1); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && tagInput.trim()) { e.preventDefault(); handleAddNewTag(tagInput.trim()); setTagInput(''); setShowTagDropdown(false); }
                        if (e.key === 'Escape') { setShowTagDropdown(false); }
                      }}
                      onBlur={() => { setTimeout(() => setShowTagDropdown(false), 200); }}
                      placeholder={newTags.length > 0 ? '+' : 'Add tags...'}
                      style={{ width: '100%', padding: '6px 0', background: 'transparent', border: 'none', color: '#e5e5e5', fontSize: '12px', outline: 'none', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
                      onFocus={() => { if (tagInput.length >= 1) setShowTagDropdown(true); }}
                    />
                    {showTagDropdown && tagSuggestions.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, marginTop: '4px', borderRadius: '10px', overflow: 'hidden', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 12px 40px rgba(0,0,0,0.6)', maxHeight: '200px', overflowY: 'auto' }}>
                        {tagSuggestions.map((t, i) => (
                          <button key={i}
                            onMouseDown={(e) => { e.preventDefault(); handleAddNewTag(t); setTagInput(''); setShowTagDropdown(false); }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', border: 'none', background: 'transparent', color: '#e5e5e5', fontSize: '13px', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s ease', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(66, 219, 123, 0.1)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <span style={{ color: '#42db7b', fontWeight: 600 }}>{t}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px',
                borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: '13px', transition: 'all 0.15s ease', background: 'transparent' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    {uploadingImage ? 'Uploading...' : ''}
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage} />
                  </label>
                  <input type="url" value={newImage}
                    onChange={(e) => { const url = e.target.value; setNewImage(url); if (url && !newImages.includes(url)) { setNewImages(prev => [...prev, url]); setNewImage(''); } }}
                    placeholder="Image URL"
                    style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', color: '#e5e5e5', fontSize: '12px', outline: 'none', width: '120px', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {draftSaved && (
                    <span style={{ fontSize: '11px', color: '#42db7b', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', transition: 'opacity 0.3s ease' }}>
                      Draft saved
                    </span>
                  )}
                  <button
                    onClick={handleSaveDraft}
                    disabled={(!newTitle.trim() && !newPost.trim()) || draftSaving}
                    style={{
                      padding: '7px 14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: (!newTitle.trim() && !newPost.trim()) ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.55)',
                      fontSize: '13px', fontWeight: 500, cursor: (!newTitle.trim() && !newPost.trim()) ? 'not-allowed' : 'pointer', borderRadius: '8px', transition: 'all 0.15s ease',
                      fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    }}
                    onMouseEnter={(e) => { if (newTitle.trim() || newPost.trim()) { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    {draftSaving ? 'Saving...' : 'Save draft'}
                  </button>
                  <button
                    onClick={handlePost}
                    disabled={(!newTitle.trim() && !newPost.trim()) || uploadingImage}
                    style={{
                      padding: '7px 20px', border: 'none', borderRadius: '20px',
                      background: (!newTitle.trim() && !newPost.trim()) || uploadingImage ? 'rgba(66, 219, 123, 0.15)' : '#42db7b',
                      color: (!newTitle.trim() && !newPost.trim()) || uploadingImage ? 'rgba(255,255,255,0.25)' : '#000',
                      fontSize: '13px', fontWeight: 600, cursor: (!newTitle.trim() && !newPost.trim()) || uploadingImage ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    }}
                  >
                    {uploadingImage ? 'Uploading...' : 'Publish'}
                  </button>
                </div>
              </div>

              {/* Keyboard shortcuts hint */}
              <div style={{
                padding: '6px 20px 10px',
                display: 'flex', gap: '16px',
                borderTop: '1px solid rgba(255,255,255,0.03)',
              }}>
                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.18)', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                  <kbd style={{ padding: '1px 4px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', fontSize: '10px' }}>⌘B</kbd> bold
                </span>
                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.18)', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                  <kbd style={{ padding: '1px 4px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', fontSize: '10px' }}>⌘S</kbd> save draft
                </span>
                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.18)', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                  <kbd style={{ padding: '1px 4px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', fontSize: '10px' }}>⌘Enter</kbd> publish
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditing && editingPostId && isAdmin && (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
              paddingTop: '8vh',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            <div
              style={{
                width: '100%', maxWidth: '600px', margin: '0 16px',
                background: '#1e1e1e', borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 24px 80px rgba(0, 0, 0, 0.6)',
                overflow: 'visible', position: 'relative',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
              }}>
                <span style={{ color: '#fff', fontSize: '14px', fontWeight: 600, fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                  Edit post
                </span>
                <button
                  onClick={() => { setIsEditing(false); setEditingPostId(null); setEditTitle(''); setEditContent(''); setEditImage(''); setEditImages([]); setEditTags([]); setEditTagInput(''); setShowEditTagDropdown(false); }}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'all 0.15s ease', fontSize: '18px' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                >×</button>
              </div>

              {/* Modal body */}
              <div
                className="custom-dropdown-scrollbar"
                style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto', overscrollBehavior: 'contain' }}
                onPaste={handlePaste}
              >
                <textarea
                  value={editTitle}
                  ref={(el) => {
                    if (el) {
                      el.style.height = '0';
                      el.style.height = el.scrollHeight + 'px';
                    }
                  }}
                  onChange={(e) => {
                    setEditTitle(e.target.value);
                    e.target.style.height = '0';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onPaste={handlePaste}
                  placeholder="Title"
                  autoFocus
                  rows={1}
                  style={{
                    width: '100%',
                    padding: '0 0 12px 0',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                    color: '#facc15',
                    fontSize: '18px',
                    fontWeight: 600,
                    outline: 'none',
                    fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    marginBottom: '12px',
                    resize: 'none',
                    overflow: 'hidden',
                  }}
                />
                <textarea
                  value={editContent}
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                  onChange={(e) => {
                    setEditContent(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onKeyDown={(e) => handleRichTextKeyDown(e, editContent, setEditContent)}
                  onPaste={handlePaste}
                  placeholder="What's on your mind?"
                  style={{ width: '100%', padding: '0', background: 'transparent', border: 'none', color: '#e5e5e5', fontSize: '15px', fontWeight: 400, lineHeight: '1.6', fontFamily: '-apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Inter\", sans-serif', resize: 'none' as const, minHeight: '220px', outline: 'none' }}
                  rows={1}
                />

                {editImages.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                    {editImages.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        <img src={img} alt={`Preview ${idx+1}`}
                          style={{ maxWidth: '160px', maxHeight: '120px', objectFit: 'contain', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}
                          onClick={() => setSelectedImage(img)}
                        />
                        <button onClick={() => setEditImages((prev: string[]) => prev.filter((_: string, i: number) => i !== idx))}
                          style={{ position: 'absolute', top: '4px', right: '4px', width: '18px', height: '18px', background: 'rgba(220, 38, 38, 0.9)', border: 'none', borderRadius: '50%', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', lineHeight: 1 }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                {editTags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                    {editTags.map((tag, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '16px', background: 'rgba(66, 219, 123, 0.12)', border: '1px solid rgba(66, 219, 123, 0.25)', color: '#42db7b', fontSize: '12px', fontWeight: 600, fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                        <span>{tag}</span>
                        <button onClick={() => handleRemoveEditTag(tag)}
                          style={{ background: 'none', border: 'none', color: 'rgba(66, 219, 123, 0.5)', cursor: 'pointer', padding: '0', fontSize: '13px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#42db7b'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(66, 219, 123, 0.5)'; }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ position: 'relative', marginTop: '8px' }}>
                  <input type="text" value={editTagInput}
                    onChange={(e) => { const v = e.target.value; setEditTagInput(v); setShowEditTagDropdown(v.length >= 1); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && editTagInput.trim()) { e.preventDefault(); handleAddEditTag(editTagInput.trim()); setEditTagInput(''); setShowEditTagDropdown(false); }
                      if (e.key === 'Escape') { setShowEditTagDropdown(false); }
                    }}
                    onBlur={() => { setTimeout(() => setShowEditTagDropdown(false), 200); }}
                    placeholder="Add tag"
                    style={{ width: '100%', padding: '8px 0', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', color: '#e5e5e5', fontSize: '13px', outline: 'none', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
                    onFocus={(e) => { e.currentTarget.style.borderBottomColor = 'rgba(66, 219, 123, 0.3)'; if (editTagInput.length >= 1) setShowEditTagDropdown(true); }}
                  />
                  {showEditTagDropdown && tagSuggestions.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, marginTop: '4px', borderRadius: '10px', overflow: 'hidden', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 12px 40px rgba(0,0,0,0.6)', maxHeight: '200px', overflowY: 'auto' }}>
                      {tagSuggestions.map((t, i) => (
                        <button key={i}
                          onMouseDown={(e) => { e.preventDefault(); handleAddEditTag(t); setEditTagInput(''); setShowEditTagDropdown(false); }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', border: 'none', background: 'transparent', color: '#e5e5e5', fontSize: '13px', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s ease', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(66, 219, 123, 0.1)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ color: '#42db7b', fontWeight: 600 }}>{t}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal footer */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', borderTop: '1px solid rgba(255, 255, 255, 0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', fontSize: '13px', transition: 'all 0.15s ease', background: 'transparent' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    {uploadingImage ? 'Uploading...' : ''}
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage} />
                  </label>
                  <input type="url" value={editImage}
                    onChange={(e) => { const url = e.target.value; setEditImage(url); if (url && !editImages.includes(url)) { setEditImages(prev => [...prev, url]); setEditImage(''); } }}
                    placeholder="Image URL"
                    style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', color: '#e5e5e5', fontSize: '12px', outline: 'none', width: '120px', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => { setIsEditing(false); setEditingPostId(null); setEditTitle(''); setEditContent(''); setEditImage(''); setEditImages([]); setEditTags([]); setEditTagInput(''); setShowEditTagDropdown(false); }}
                    style={{ padding: '7px 16px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', borderRadius: '8px', transition: 'all 0.15s ease', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    Cancel
                  </button>
                  <button onClick={handleSaveEdit} disabled={(!editTitle.trim() && !editContent.trim()) || uploadingImage || savingEdit}
                    style={{
                      padding: '7px 20px', border: 'none', borderRadius: '20px',
                      background: (!editTitle.trim() && !editContent.trim()) || uploadingImage || savingEdit ? 'rgba(66, 219, 123, 0.2)' : '#42db7b',
                      color: (!editTitle.trim() && !editContent.trim()) || uploadingImage || savingEdit ? 'rgba(255,255,255,0.3)' : '#000',
                      fontSize: '13px', fontWeight: 600, cursor: (!editTitle.trim() && !editContent.trim()) || uploadingImage || savingEdit ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease', fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    }}
                  >
                    {savingEdit ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: '24px 0', color: '#f87171', fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ padding: '60px 0', display: 'flex', justifyContent: 'center' }}>
            <BrailleSpinner />
          </div>
        )}

        {/* Posts Feed */}
        {!loading && (
          <>
            {filteredPosts.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
                {searchQuery || selectedTag ? 'No matching posts' : 'No posts yet'}
              </div>
            ) : (
              <div style={{ marginTop: 20 }}>
                {postsToShow.map((post) => (
                  <article
                    key={post.id}
                    style={{
                      position: 'relative',
                      padding: '18px 32px',
                      marginBottom: 20,
                      ...HUB_PANEL_SURFACE,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      const sel = window.getSelection();
                      if (target.closest('button') || target.closest('a') || target.closest('img') || (sel && sel.toString().length > 0)) return;
                      if (e.ctrlKey || e.metaKey) { window.open(watchtowerPostPath(post), '_blank'); return; }
                      sessionStorage.setItem('watchtowerScrollPosition', window.scrollY.toString());
                      sessionStorage.setItem('watchtowerDisplayCount', displayCount.toString());
                      router.push(watchtowerPostPath(post));
                    }}
                    onAuxClick={(e: React.MouseEvent) => {
                      if (e.button === 1) { const t = e.target as HTMLElement; if (t.closest('button') || t.closest('a')) return; e.preventDefault(); window.open(watchtowerPostPath(post), '_blank'); }
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; e.currentTarget.style.background = 'rgba(255,255,255,0.045)'; router.prefetch(watchtowerPostPath(post)); }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.035)'; }}
                  >
                    {/* Meta line: date + tags */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: '"SF Mono", "Fira Code", "Fira Mono", Menlo, monospace', letterSpacing: '0.02em', flexShrink: 0 }}>
                        {formatDate(post.published_at)}
                      </span>
                      {post.tags && post.tags.length > 0 && post.tags.map((tag, i) => (
                        <button
                          key={i}
                          onClick={(e) => { e.stopPropagation(); handleTagClick(tag); }}
                          style={{
                            background: selectedTag === tag ? '#3bd576' : 'none',
                            border: 'none', cursor: 'pointer',
                            color: selectedTag === tag ? '#000' : 'rgba(59,213,118,0.6)',
                            fontSize: 11, fontWeight: 500, borderRadius: 3,
                            padding: selectedTag === tag ? '0 5px' : '0',
                            transition: 'color 0.1s',
                          }}
                          onMouseEnter={(e) => { if (selectedTag !== tag) e.currentTarget.style.color = '#3bd576'; }}
                          onMouseLeave={(e) => { if (selectedTag !== tag) e.currentTarget.style.color = 'rgba(59,213,118,0.6)'; }}
                        >
                          #{tag}
                        </button>
                      ))}
                      {isAdmin && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 2, flexShrink: 0 }}>
                          <button onClick={(e) => { e.stopPropagation(); handleStartEdit(post.id, post.title || '', post.content || '', post.image_url || null, post.image_urls || null, post.tags || null); }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.15)', cursor: 'pointer', fontSize: 13, padding: '2px 4px', borderRadius: 4, transition: 'color 0.1s' }} onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }} onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.15)'; }}>✎</button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeletePost(post.id); }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.15)', cursor: 'pointer', fontSize: 13, padding: '2px 4px', borderRadius: 4, transition: 'color 0.1s' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#f87171'; }} onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.15)'; }}>✕</button>
                        </div>
                      )}
                    </div>

                    {/* Title */}
                    <h3 style={{
                      fontSize: 21, fontWeight: 600, color: '#E5E7EB',
                      letterSpacing: '-0.015em', lineHeight: 1.35, margin: '0 0 8px',
                      fontFamily: 'var(--font-inter-tight), -apple-system, BlinkMacSystemFont, sans-serif',
                    }}>
                      {renderTitleWithScaledEmojis(post.title || '')}
                    </h3>

                    {/* Content preview */}
                    {post.content && (
                      <div style={{ marginBottom: 0 }}
                        onClick={(e) => { const s = window.getSelection(); if (s && s.toString().length > 0) e.stopPropagation(); }}
                      >
                        {(() => {
                          const charLimit = 400;
                          const isExpanded = expandedPosts[post.id];
                          const canToggle = post.content.length > charLimit;
                          const isTruncated = canToggle && !isExpanded;
                          const displayContent = isTruncated ? post.content.slice(0, charLimit).replace(/\s+\S*$/, '...') : post.content;
                          return (
                            <>
                              {renderContentWithHighlights(displayContent)}
                              {canToggle && (
                                <span
                                  onClick={(e) => { e.stopPropagation(); setExpandedPosts(prev => ({ ...prev, [post.id]: !prev[post.id] })); }}
                                  style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, cursor: 'pointer', marginLeft: 4, transition: 'color 0.1s' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.color = '#3bd576'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
                                >
                                  {isExpanded ? '↑ less' : '↓ more'}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Images */}
                    {(() => {
                      let imgs: string[] = [];
                      if (post.image_urls && post.image_urls.length > 0) {
                        imgs = [...new Set(post.image_urls)];
                        if (post.image_url && !imgs.includes(post.image_url)) imgs.push(post.image_url);
                      } else if (post.image_url) {
                        imgs = [post.image_url];
                      }
                      return imgs.length > 0 ? (
                        <div style={{ marginTop: 10 }}>
                          {imgs.map((url, i) => (
                            <div key={i} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', background: '#0a0a0a', marginBottom: 6, cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); setSelectedImage(url); }}
                            >
                              <img src={url} alt={post.image_alt || ''} style={{ width: '100%', height: 'auto', maxHeight: '50vh', objectFit: 'contain', display: 'block' }} onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}

                    {/* Footer: comment count + share */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                      {post.comment_count > 0 && (
                        <a
                          href={watchtowerPostPath(post)}
                          onClick={(e) => { e.stopPropagation(); if (e.ctrlKey || e.metaKey) return; e.preventDefault(); sessionStorage.setItem('watchtowerScrollPosition', window.scrollY.toString()); sessionStorage.setItem('watchtowerDisplayCount', displayCount.toString()); router.push(watchtowerPostPath(post)); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'rgba(255,255,255,0.25)', fontSize: 12, textDecoration: 'none', transition: 'color 0.1s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#3bd576'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.25)'; }}
                        >
                          <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                          <span>{post.comment_count}</span>
                        </a>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleShare(post); }}
                        style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', cursor: 'pointer', padding: 0, transition: 'color 0.1s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#3bd576'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.2)'; }}
                        title="Share"
                      >
                        <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      </button>
                    </div>
                  </article>
                ))}

                {hasMore && (
                  <div style={{ padding: '16px 0', textAlign: 'center' }}>
                    <button
                      onClick={handleLoadMore}
                      style={{
                        background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
                        fontSize: 13, cursor: 'pointer', padding: '6px 12px',
                        transition: 'color 0.1s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#3bd576'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
                    >
                      Load more ↓
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setSelectedImage(null)}
        >
          <div style={{ position: 'relative', maxWidth: '95vw', maxHeight: '95vh' }} onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="Full size" style={{ maxWidth: '100%', maxHeight: '95vh', objectFit: 'contain', borderRadius: 8 }} />
            <button
              onClick={() => setSelectedImage(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', cursor: 'pointer', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}
            >×</button>
          </div>
        </div>
      )}
    </div>
  );
}
